/**
 * pages/DaftarBarang.js
 * Item list — grouped by category, collapsible. All filters (tab, search, low-stock)
 * apply first; grouping is applied to whatever `visible` already contains.
 */
import React, { useMemo, useState } from "react";
import { fmtQty } from "../utils/idGenerators";
import { parseFitment } from "../utils/fitment";
import { parseMarketCodes } from "../utils/marketCodes";
import { EyeIcon, PencilIcon, TrashIcon } from "../components/Icons";
import { stockStatus, itemTotal } from "../utils/stock";

// ── Task 5a/5a-b: cheap render-scale guard, pre-backend (DELETE during backend pagination
// migration) ── Post-import (2,351 items) rendering every row as DOM felt slow. This is a
// disposable comfort fix, not a real solution — server-side pagination in the backend
// migration replaces both constants and everything that reads them. 5a-b removed the
// per-group 100-row cap (user feedback: an expanded group should show ALL its rows — one
// group's worth is not a perf problem; the real protection is staying collapsed by default).
const LARGE_DATASET_THRESHOLD = 300; // above this, groups start collapsed on load
const SEARCH_TOTAL_ROW_CAP = 300;    // total rows rendered across all groups while searching

export default function DaftarBarang({ items, onOpenItem, onAddItem, onAddSet, onEdit, onDelete, lowStockThreshold = 0, onSetThreshold, categories = [], onManageCategories, categoryVisits = {}, onCategoryVisit, onImport }) {
  const [q, setQ] = useState("");
  const [view, setView] = useState("spare"); // "spare" (isSet false) | "set" (isSet true)
  const [searchBy, setSearchBy] = useState("semua"); // semua | nama | kodePasar | kodeInternal
  const [thrInput, setThrInput] = useState(String(lowStockThreshold ?? 0));
  const [onlyLow, setOnlyLow] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false); // stock notification section — collapsed by default
  // Lazy-initialized ONCE from the full items prop at mount ("on page load") — large
  // datasets start fully collapsed; small ones (e.g. the 7-item seed) start open, exactly
  // as before. Never re-runs when `items` changes later — collapse stays user-controlled.
  const [collapsed, setCollapsed] = useState(() => {
    if (items.length <= LARGE_DATASET_THRESHOLD) return new Set();
    const names = new Set();
    for (const it of items) {
      names.add(((it.category || "").trim() || "Tanpa Kategori").toUpperCase());
    }
    return names;
  });

  const codeByName = useMemo(() => {
    const m = new Map();
    for (const c of categories) m.set(String(c.name).trim().toUpperCase(), c.code);
    return m;
  }, [categories]);

  const filtered = useMemo(() => {
    const inView = items.filter((it) => (view === "set" ? it.isSet === true : it.isSet !== true));
    const needle = q.trim().toLowerCase();
    if (!needle) return inView;
    const fieldsFor = (it) => {
      switch (searchBy) {
        case "nama": return [it.name];
        case "kodePasar": return parseMarketCodes(it.marketCodes ?? it.marketCode);
        case "kodeInternal": return [it.internalCode];
        case "kategori": return [it.category];
        default: return [it.name, ...parseMarketCodes(it.marketCodes ?? it.marketCode), it.internalCode, it.category]; // "semua"
      }
    };
    return inView.filter((it) =>
      fieldsFor(it).filter(Boolean).some((f) => f.toLowerCase().includes(needle))
    );
  }, [items, q, view, searchBy]);

  const attention = useMemo(
    () => filtered.filter((it) => stockStatus(it, lowStockThreshold) !== "ok"),
    [filtered, lowStockThreshold]
  );
  const lowCount = attention.length;
  const visible = onlyLow ? attention : filtered;

  // Stock notification section — scans ALL items (both Spare Parts and SET, regardless of the
  // current tab/search) so it reads as a real notification center, not just "what the current
  // view happens to show." Negative sorted most-negative-first (most prominent); out/low
  // alphabetically. Does NOT filter the table — see the "Lihat yang menipis" toggle above for
  // that; this is a separate, informational jump-to-item list (clicking a row opens it, same
  // as clicking a row in the main table below).
  const notifGroups = useMemo(() => {
    const negative = [], out = [], low = [];
    for (const it of items) {
      const status = stockStatus(it, lowStockThreshold);
      if (status === "negative") negative.push(it);
      else if (status === "out") out.push(it);
      else if (status === "low") low.push(it);
    }
    negative.sort((a, b) => itemTotal(a) - itemTotal(b)); // most negative first
    const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
    out.sort(byName);
    low.sort(byName);
    return { negative, out, low };
  }, [items, lowStockThreshold]);
  const notifTotal = notifGroups.negative.length + notifGroups.out.length + notifGroups.low.length;

  const renderNotifRow = (it) => (
    <div key={it.internalCode} className="notif-row" onClick={() => onOpenItem(it.internalCode)}
      style={{ display: "flex", justifyContent: "space-between", gap: "var(--gap-md)", padding: "6px 0", cursor: "pointer", borderBottom: "1px solid #f3e8c9" }}>
      <span>{it.name} <span className="mono" style={{ color: "var(--muted)" }}>({it.internalCode})</span></span>
      <span className="num mono" style={{ fontWeight: 600 }}>{fmtQty(itemTotal(it))}</span>
    </div>
  );

  const renderNotifGroup = (title, list) => (
    list.length > 0 && (
      <div key={title} style={{ marginBottom: "var(--gap-md)" }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: "var(--gap-xs)" }}>{title} ({list.length})</div>
        {list.map(renderNotifRow)}
      </div>
    )
  );

  const groups = useMemo(() => {
    const byCat = new Map();
    for (const it of visible) {
      const name = (it.category || "").trim() || "Tanpa Kategori";
      const key = name.toUpperCase();
      if (!byCat.has(key)) byCat.set(key, { name, code: codeByName.get(key) || "—", items: [] });
      byCat.get(key).items.push(it);
    }
    return [...byCat.values()].sort((a, b) => {
      if (a.name === "Tanpa Kategori") return 1;
      if (b.name === "Tanpa Kategori") return -1;
      return a.name.localeCompare(b.name);
    });
  }, [visible, codeByName]);

  const toggle = (name) => {
    const key = String(name).toUpperCase();
    const willOpen = collapsed.has(key);
    setCollapsed((prev) => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
    if (willOpen) onCategoryVisit?.(name);
  };

  const anyOpen = groups.some((g) => !collapsed.has(g.name.toUpperCase()));

  // Task 5a: while a search query is active, every group with a match renders expanded
  // regardless of `collapsed` — matches must be reachable without manually clicking through
  // collapsed groups first. `collapsed` itself is never touched here, so clearing the query
  // reverts every group to exactly the collapse state it had before searching.
  const isSearching = q.trim().length > 0;

  const catAnchor = (name) => `cat-${String(name).toUpperCase().replace(/\s+/g, "-")}`;

  const jumpTo = (name) => { setSearchBy("kategori"); setQ(name); onCategoryVisit?.(name); };

  const rankedGroups = useMemo(() => {
    const visitOf = (g) => categoryVisits[g.name.toUpperCase()] || 0;
    return [...groups]
      .sort((a, b) => {
        const va = visitOf(a), vb = visitOf(b);
        if (vb !== va) return vb - va;
        return a.name.localeCompare(b.name);
      })
      .slice(0, 8);
  }, [groups, categoryVisits]);

  const renderRow = (it) => {
    const total = (Number(it.stokPusat) || 0) + (Number(it.stokCabang) || 0);
    const status = stockStatus(it, lowStockThreshold);
    const marketCodes = it.marketCodes || [];
    return (
      <tr key={it.internalCode} onClick={() => onOpenItem(it.internalCode)}>
        <td className="mono">{it.internalCode}</td>
        <td>{marketCodes.length === 0
          ? <span className="chip chip--muted">—</span>
          : marketCodes.map((code, i) => (
              <span key={`${code}-${i}`} className="chip chip--muted" style={{ marginRight: "var(--gap-xs)" }}>{code}</span>
            ))}</td>
        <td>
          {it.name}
          {it.isSet === true && <span className="chip chip--set" style={{ marginLeft: "var(--gap-sm)" }}>SET</span>}
          {(status === "out" || status === "negative") && <span className="chip chip--out" style={{ marginLeft: "var(--gap-sm)" }}>Habis</span>}
          {status === "low" && <span className="chip chip--low" style={{ marginLeft: "var(--gap-sm)" }}>Stok rendah</span>}
        </td>
        <td>{it.category || "—"}</td>
        <td className="ket-cell" title={it.notes || ""}>{it.notes || "—"}</td>
        <td>{(() => { const cars = parseFitment(it.fitment); return cars.length === 0 ? "—" : cars.map((c) => <span key={c} className="chip chip--muted" style={{ marginRight: "var(--gap-xs)" }}>{c}</span>); })()}</td>
        <td className="num">{fmtQty(it.stokPusat)}</td>
        <td className="num">{fmtQty(it.stokCabang)}</td>
        <td className="num total-cell">{fmtQty(total)}</td>
        <td className="row-actions" onClick={(e) => e.stopPropagation()}>
          <button className="btn btn-sm btn-ghost" onClick={() => onOpenItem(it.internalCode)}><EyeIcon /> Detail</button>
          <button className="btn btn-sm btn-ghost" onClick={() => onEdit(it)}><PencilIcon /> Ubah</button>
          <button className="btn btn-sm btn-danger-ghost" onClick={() => onDelete(it)}><TrashIcon /> Hapus</button>
        </td>
      </tr>
    );
  };

  const thead = (
    <thead>
      <tr>
        <th>Kode Internal</th>
        <th>Kode Pasar</th>
        <th>Nama</th>
        <th>Kategori</th>
        <th>Keterangan</th>
        <th>Tipe Mobil</th>
        <th className="num">Stok Pusat</th>
        <th className="num">Stok Cabang</th>
        <th className="num">Total</th>
        <th>Aksi</th>
      </tr>
    </thead>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Daftar Barang</h1>
          <div className="page-sub">{visible.length} barang</div>
        </div>
        <div style={{ display: "flex", gap: "var(--gap-sm)" }}>
          <button className="btn" onClick={onManageCategories}>Kelola Kategori</button>
          <button className="btn" onClick={onImport}>Impor Data</button>
          <button className="btn" onClick={onAddItem}>+ Tambah Barang</button>
          <button className="btn btn-primary" onClick={onAddSet}>+ Tambah SET</button>
        </div>
      </div>

      <div className="toolbar">
        <div className="seg">
          <button type="button" className={view === "spare" ? "active" : ""} onClick={() => setView("spare")}>Spare Parts</button>
          <button type="button" className={view === "set" ? "active" : ""} onClick={() => setView("set")}>SET</button>
        </div>
        <select className="form-select search-by" value={searchBy} onChange={(e) => setSearchBy(e.target.value)}>
          <option value="semua">Semua</option>
          <option value="nama">Nama</option>
          <option value="kodePasar">Kode Pasar</option>
          <option value="kodeInternal">Kode Internal</option>
          <option value="kategori">Kategori</option>
        </select>
        <input
          className="form-input grow"
          placeholder={{
            semua: "Cari nama, kode, kategori…",
            nama: "Cari nama…",
            kodePasar: "Cari kode pasar…",
            kodeInternal: "Cari kode internal…",
            kategori: "Cari kategori…",
          }[searchBy]}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <label style={{ display: "flex", alignItems: "center", gap: "var(--gap-sm)", whiteSpace: "nowrap" }}>
          Stok min. umum
          <input className="form-input" type="number" min="0" style={{ width: 72 }}
            value={thrInput}
            onChange={(e) => { setThrInput(e.target.value); onSetThreshold(e.target.value); }} />
        </label>
        {groups.length > 0 && (
          <button type="button" className="btn btn-sm"
            onClick={() => {
              if (anyOpen) setCollapsed(new Set(groups.map((g) => g.name.toUpperCase())));
              else setCollapsed(new Set());
            }}>
            {anyOpen ? "Tutup semua" : "Buka semua"}
          </button>
        )}
      </div>

      {notifTotal > 0 && (
        <div className="card" style={{ marginBottom: "var(--gap-md)", padding: 0, overflow: "hidden" }}>
          <button type="button" onClick={() => setNotifOpen((v) => !v)}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: "var(--gap-sm)",
              padding: "10px 14px", background: "#fef3c7", color: "#92400e",
              border: "none", cursor: "pointer", textAlign: "left", font: "inherit",
            }}>
            <span>{notifOpen ? "▾" : "▸"}</span>
            <b>{notifTotal} barang butuh perhatian</b>
            <span style={{ fontWeight: 400 }}>
              {[
                notifGroups.negative.length > 0 && `${notifGroups.negative.length} stok negatif`,
                notifGroups.out.length > 0 && `${notifGroups.out.length} habis`,
                notifGroups.low.length > 0 && `${notifGroups.low.length} menipis`,
              ].filter(Boolean).join(" · ")}
            </span>
          </button>
          {notifOpen && (
            <div style={{ padding: "10px 14px 4px" }}>
              {renderNotifGroup("Stok Negatif", notifGroups.negative)}
              {renderNotifGroup("Habis (Stok 0)", notifGroups.out)}
              {renderNotifGroup("Stok Menipis", notifGroups.low)}
            </div>
          )}
        </div>
      )}

      {(lowCount > 0 || onlyLow) && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--gap-md)",
          background: "#fef3c7", color: "#92400e",
          padding: "10px 14px", borderRadius: 8, marginBottom: "var(--gap-md)",
        }}>
          <span>
            {lowCount > 0
              ? `${lowCount} barang stok menipis (rendah atau habis).`
              : "Tidak ada barang menipis di tampilan ini."}
          </span>
          <button type="button" className="btn btn-sm" style={{ flexShrink: 0 }}
            onClick={() => setOnlyLow((v) => !v)}>
            {onlyLow ? "Tampilkan semua" : "Lihat yang menipis"}
          </button>
        </div>
      )}

      {groups.length > 0 && (
        <div className="cat-jumpbar">
          <span className="cat-jumpbar__label">Lompat ke:</span>
          {rankedGroups.map((g) => (
            <button type="button" key={g.name} className="cat-jumpbar__chip" onClick={() => jumpTo(g.name)}>
              {g.name}
            </button>
          ))}
        </div>
      )}

      {groups.length === 0 ? (
        <div className="table-wrap">
          <table className="table">
            <tbody>
              <tr><td colSpan={10} className="empty-state">Tidak ada barang cocok.</td></tr>
            </tbody>
          </table>
        </div>
      ) : (() => {
        // Task 5a: `searchBudget` is a plain running counter across the groups.map below (not
        // React state) — smallest possible change for a fix that's getting deleted anyway.
        let searchBudget = SEARCH_TOTAL_ROW_CAP;
        return groups.map((group) => {
          const isOpen = isSearching || !collapsed.has(group.name.toUpperCase());
          // 5a-b: no per-group cap — an expanded, non-searching group always renders every
          // row. Only the shared search budget below can still cap what's shown.
          let rows = group.items;
          let capNote = null;
          if (isOpen && isSearching) {
            const take = Math.min(group.items.length, searchBudget);
            rows = group.items.slice(0, take);
            searchBudget -= take;
            if (take < group.items.length) {
              capNote = `Menampilkan ${fmtQty(take)} dari ${fmtQty(group.items.length)} — gunakan pencarian.`;
            }
          }
          return (
            <div className={"cat-group" + (isOpen ? " is-open" : "")} key={group.name} id={catAnchor(group.name)}>
              <button type="button" className="cat-group__head" onClick={() => toggle(group.name)}>
                <span className={"cat-group__chevron" + (isOpen ? " is-open" : "")}>▸</span>
                <span className="cat-group__name">{group.name}</span>
                <span className="cat-group__code mono">{group.code}</span>
                <span className="cat-group__count">{group.items.length} barang</span>
              </button>
              {isOpen && rows.length > 0 && (
                <div className="table-wrap">
                  <table className="table">
                    {thead}
                    <tbody>{rows.map(renderRow)}</tbody>
                  </table>
                </div>
              )}
              {isOpen && capNote && (
                <div className="form-hint" style={{ padding: "8px 4px" }}>{capNote}</div>
              )}
            </div>
          );
        });
      })()}
    </div>
  );
}
